import React, { useState } from "react";
import { inventoryRepo, stockMoveRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Badge, Button, DataTable, Input, Modal, PageHead, StatCard, Tabs, formatDate, useAsync, useTableState } from "../../components/ui";
import Icon from "../../components/ui/Icon";

export default function Inventory() {
  const { orgId, branchId, toast } = useApp();
  const { tx } = useI18n();
  const [tab, setTab] = useState("stock");
  const tableState = useTableState();
  const [open, setOpen] = useState(null);
  const [qty, setQty] = useState(1);

  const { data: stock, loading, reload } = useAsync(
    () => inventoryRepo.list(orgId, { page: tableState.page, perPage: tableState.perPage, search: tableState.search, filter: (row) => !branchId || row.branchId === branchId }),
    [orgId, branchId, tableState.page, tableState.perPage, tableState.search]
  );
  const { data: moves, reload: reloadMoves } = useAsync(
    () => stockMoveRepo.list(orgId, { page: 1, perPage: 20, sortBy: "createdAt", sortDir: "desc", filter: (row) => !branchId || row.branchId === branchId }),
    [orgId, branchId],
    { rows: [] }
  );

  const lowStock = (stock?.rows || []).filter((row) => row.quantity <= row.reorderPoint).length;

  const adjust = async (type) => {
    if (!open) return;
    const delta = type === "out" ? -qty : qty;
    await inventoryRepo.update(orgId, open.id, { quantity: Math.max(0, open.quantity + delta), lastCountedAt: new Date().toISOString() });
    await stockMoveRepo.create(orgId, { productId: open.productId, productName: open.productName, branchId: open.branchId, type, quantity: delta, reason: type === "in" ? tx("รับเข้าสต็อก (ปรับด้วยมือ)", "Manual stock in") : tx("ตัดจ่ายสต็อก (ปรับด้วยมือ)", "Manual stock out") });
    setOpen(null);
    setQty(1);
    reload();
    reloadMoves();
    toast(tx("ปรับสต็อกเรียบร้อย", "Stock updated"), "success");
  };

  return (
    <>
      <PageHead title={tx("คุมสต็อกสินค้า", "Inventory control")} subtitle={tx("รับเข้า-ตัดจ่าย พร้อมแจ้งเตือนจุดสั่งซื้อซ้ำ", "Stock in/out with reorder alerts")} />

      <div className="stat-grid">
        <StatCard icon={<Icon name="box" />} label={tx("รายการสินค้า", "SKUs")} value={stock?.total || 0} />
        <StatCard icon={<Icon name="warning" />} label={tx("ใกล้หมด (ต่ำกว่าจุดสั่งซื้อ)", "Below reorder point")} value={lowStock} tone={lowStock ? "var(--c-rose)" : undefined} />
      </div>

      <Tabs tabs={[{ key: "stock", label: tx("สต็อกปัจจุบัน", "Current stock") }, { key: "moves", label: tx("ประวัติเคลื่อนไหว", "Stock moves") }]} value={tab} onChange={setTab} />

      {tab === "stock" ? (
        <DataTable
          loading={loading}
          rows={stock?.rows || []}
          total={stock?.total || 0}
          page={tableState.page}
          perPage={tableState.perPage}
          onPage={tableState.setPage}
          onPerPage={tableState.setPerPage}
          emptyText={tx("ยังไม่มีสินค้าในสต็อก", "No stock items")}
          columns={[
            { key: "productName", label: tx("สินค้า", "Product") },
            {
              key: "quantity",
              label: tx("คงเหลือ", "Qty"),
              align: "right",
              render: (row) => (
                <span className={row.quantity <= row.reorderPoint ? "u-danger u-strong" : ""}>{row.quantity} {row.unit}</span>
              ),
            },
            { key: "reorderPoint", label: tx("จุดสั่งซื้อซ้ำ", "Reorder point"), align: "right" },
            { key: "lastCountedAt", label: tx("นับล่าสุด", "Last counted"), render: (row) => formatDate(row.lastCountedAt) },
            { key: "action", label: "", render: (row) => <Button size="sm" onClick={() => setOpen(row)}>{tx("ปรับสต็อก", "Adjust")}</Button> },
          ]}
        />
      ) : (
        <DataTable
          rows={moves?.rows || []}
          emptyText={tx("ยังไม่มีความเคลื่อนไหว", "No movements")}
          columns={[
            { key: "createdAt", label: tx("วันที่", "Date"), render: (row) => formatDate(row.createdAt, true) },
            { key: "productName", label: tx("สินค้า", "Product") },
            { key: "type", label: tx("ประเภท", "Type"), render: (row) => <Badge tone={row.type === "in" ? "green" : row.type === "out" ? "red" : "amber"}>{row.type}</Badge> },
            { key: "quantity", label: tx("จำนวน", "Qty"), align: "right", render: (row) => (row.quantity > 0 ? `+${row.quantity}` : row.quantity) },
            { key: "reason", label: tx("เหตุผล", "Reason") },
          ]}
        />
      )}

      <Modal
        open={!!open}
        title={tx(`ปรับสต็อก: ${open?.productName || ""}`, `Adjust: ${open?.productName || ""}`)}
        size="sm"
        onClose={() => setOpen(null)}
        footer={
          <>
            <Button variant="danger" onClick={() => adjust("out")}>{tx("ตัดจ่ายออก", "Stock out")}</Button>
            <Button variant="success" onClick={() => adjust("in")}>{tx("รับเข้า", "Stock in")}</Button>
          </>
        }
      >
        <Input label={tx("จำนวน", "Quantity")} type="number" min={1} value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value)))} />
        <div className="u-fs-13 u-muted">{tx("คงเหลือปัจจุบัน", "Current stock")}: {open?.quantity} {open?.unit}</div>
      </Modal>
    </>
  );
}
